import {
  Button,
  H5,
  InputField,
  InputNumber,
  InputOption,
  InputSelect,
  InputText,
} from "@sellar-io/components";
import type {
  SupplierProductGetResponseChildType,
  SupplierProductGetResponseType,
} from "@sellar-io/api-v2/types";
import { MAX_PRODUCT_WEIGHT_KG, ProductTypesFlat } from "@sellar-io/common";
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import { Alert } from "../../../../components/alert/Alert";
import { DialogCTA } from "../../../../components/modal/Dialog";
import { apiV2AssertError } from "../../../../hooks/api/v2/apiV2Request";
import { useProductCreate } from "../../../../hooks/api/v2/product/useProductCreate";
import { useProductUpdate } from "../../../../hooks/api/v2/product/useProductUpdate";
import { useCanonicalWeightCheck } from "../../../../hooks/useCanonicalWeightCheck";
import { CanonicalWeightHint } from "../../../../components/canonicalWeightHint/CanonicalWeightHint";
import { ProductItemDialogPages } from "./ProductItemDialog.shared";

type ProductItemDialogProductType = Pick<
  SupplierProductGetResponseChildType,
  | "vegan"
  | "lactoseFree"
  | "glutenFree"
  | "containerType"
  | "unit"
  | "packQuantity"
  | "weight"
  | "volume"
  | "sku"
  | "id"
>;

type ParentProductItemDialogType = Pick<
  SupplierProductGetResponseType,
  "vegan" | "lactoseFree" | "glutenFree" | "id"
>;

export default function ProductItemDialogDetails({
  setStep,
  onDismiss,
  product,
  parentProduct,
  setProductId,
}: {
  setStep: (step: number) => void;
  parentProduct: ParentProductItemDialogType;
  onDismiss: () => void;
  product?: ProductItemDialogProductType;
  setProductId: (id: number) => void;
}) {
  const disabled = !!product?.id;

  const [containerType, setContainerType] = useState<string | undefined>(
    product?.containerType ?? undefined,
  );
  const [volume, setVolume] = useState<number | undefined>(
    product?.volume ?? undefined,
  );
  const [displayUnit, setDisplayUnit] = useState<string | undefined>(
    product?.unit ?? undefined,
  );
  const [packQuantity, setPackQuantity] = useState<number | undefined>(
    product?.packQuantity ?? undefined,
  );
  const [weight, setWeight] = useState<number | undefined>(
    product?.weight ?? undefined,
  );
  const [sku, setSku] = useState<string | undefined>(product?.sku ?? undefined);
  const [vegan, setVegan] = useState<boolean>(
    product?.vegan === false || product?.vegan
      ? product.vegan
      : (parentProduct.vegan ?? false),
  );
  const [lactoseFree, setLactoseFree] = useState<boolean>(
    product?.lactoseFree === false || product?.lactoseFree
      ? product.lactoseFree
      : (parentProduct.lactoseFree ?? false),
  );
  const [glutenFree, setGlutenFree] = useState<boolean>(
    product?.glutenFree === false || product?.glutenFree
      ? product.glutenFree
      : (parentProduct.glutenFree ?? false),
  );
  const [validationError, setValidationError] = useState<string | undefined>();

  const createProductMutation = useProductCreate();
  const productUpdateMutation = useProductUpdate(product?.id ?? 0);

  const activeMutation = product
    ? productUpdateMutation
    : createProductMutation;
  const hasRequestError = activeMutation.isError;
  const requestErrorMessage = hasRequestError
    ? (apiV2AssertError(activeMutation.error)?.response?.data.message ??
      "An unknown error occurred")
    : undefined;

  const errorMessage = validationError ?? requestErrorMessage;

  const { canonicalWeight, weightDeviatesFromCanonical } =
    useCanonicalWeightCheck({
      containerType,
      unit: displayUnit,
      volume,
      packQuantity,
      weight,
    });

  // Fall back to the canonical lookup for submission while nothing has been
  // entered or saved yet, without writing it into state.
  const effectiveWeight = weight ?? canonicalWeight ?? undefined;

  const onSubmit = () => {
    setValidationError(undefined);

    if (!containerType) throw new Error("containerType not defined");
    if (!volume) throw new Error("volume not defined");
    if (!displayUnit) throw new Error("unit not defined");
    if (!packQuantity) throw new Error("packQuantity not defined");

    if (weight != null && weight > MAX_PRODUCT_WEIGHT_KG) {
      setValidationError(
        `Weight cannot be more than ${MAX_PRODUCT_WEIGHT_KG}kg.`,
      );
      return;
    }

    const parameters = {
      sku,
      weight: effectiveWeight,
      vegan,
      lactoseFree,
      glutenFree,
    };

    if (product) {
      return productUpdateMutation.mutate(parameters, {
        onSuccess: () => onDismiss(),
      });
    }

    return createProductMutation.mutate(
      {
        ...parameters,
        type: "productItem",
        ParentId: parseInt(parentProduct.id.toString(), 10),
        containerType,
        volume,
        unit: displayUnit,
        packQuantity,
      },
      {
        onSuccess: (response) => {
          setProductId(response.id);
          setStep(ProductItemDialogPages.PRODUCT_PRICING);
        },
      },
    );
  };
  return (
    <>
      <form className={"flex flex-col gap-2"}>
        {errorMessage && <Alert type={"error"} message={errorMessage} />}
        <InputField label={"Container type"}>
          <InputSelect
            disabled={disabled}
            required={true}
            value={containerType}
            onChange={(e) => setContainerType(e.target.value)}
          >
            <InputOption value="" disabled hidden>
              Choose...
            </InputOption>
            {ProductTypesFlat.map((item) => (
              <option key={item.key} value={item.key}>
                {item.display}
              </option>
            ))}
          </InputSelect>
        </InputField>
        <div>
          <InputField label={"Volume"}>
            <InputNumber
              fractionDigits={1}
              disabled={disabled}
              min={1}
              required={true}
              placeholder={"330"}
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
            />
          </InputField>
          <InputField label={"Unit"}>
            <InputSelect
              disabled={disabled}
              required={true}
              value={displayUnit}
              onChange={(e) => setDisplayUnit(e.target.value)}
            >
              <InputOption value="" disabled hidden>
                Choose...
              </InputOption>
              <option value={"gal"}>gal - gallon</option>
              <option value={"L"}>L - litre</option>
              <option value={"ml"}>ml - millilitre</option>
              <option value={"cl"}>cl - centilitre</option>
              <option value={"pt"}>pt - pint</option>
              <option value={"oz"}>oz - ounce</option>
              <option value={"g"}>g - gram</option>
              <option value={"kg"}>kg - kilogram</option>
            </InputSelect>
          </InputField>
        </div>
        <InputField label={"Pack Quantity"}>
          <InputNumber
            step="1"
            fractionDigits={0}
            disabled={disabled}
            min={1}
            required={true}
            placeholder={"12"}
            value={packQuantity}
            onChange={(e) => setPackQuantity(parseInt(e.target.value, 10))}
          />
        </InputField>
        <div className="grid w-full grid-cols-2 gap-2">
          <InputField
            label={"Weight (kg)"}
            note={
              <CanonicalWeightHint
                canonicalWeight={canonicalWeight}
                weightDeviatesFromCanonical={weightDeviatesFromCanonical}
                weight={weight}
              />
            }
          >
            <InputNumber
              step={".01"}
              fractionDigits={2}
              max={MAX_PRODUCT_WEIGHT_KG}
              placeholder={canonicalWeight?.toString()}
              required={false}
              value={weight}
              onChange={(e) => {
                const parsed = parseFloat(e.target.value);
                setWeight(Number.isNaN(parsed) ? undefined : parsed);
              }}
            />
          </InputField>
          <InputField label={"SKU"}>
            <InputText
              required={false}
              value={sku}
              onChange={(e) => setSku(e.target.value)}
            />
          </InputField>
        </div>

        <div className={"divide-y rounded-lg border border-solid"}>
          <div
            className={twMerge(
              "flex flex-row justify-between p-4",
              vegan && "bg-gray-100",
            )}
            onClick={() => setVegan(!vegan)}
          >
            <H5>Vegan</H5>
            <input checked={vegan} readOnly type={"checkbox"} />
          </div>
          <div
            className={twMerge(
              "flex flex-row justify-between p-4",
              glutenFree && "bg-gray-100",
            )}
            onClick={() => setGlutenFree(!glutenFree)}
          >
            <H5>Gluten free</H5>
            <input checked={glutenFree} readOnly type={"checkbox"} />
          </div>
          <div
            className={twMerge(
              "flex flex-row justify-between p-4",
              lactoseFree && "bg-gray-100",
            )}
            onClick={() => setLactoseFree(!lactoseFree)}
          >
            <H5>Lactose free</H5>
            <input checked={lactoseFree} readOnly type={"checkbox"} />
          </div>
        </div>
      </form>
      <DialogCTA>
        <Button
          loading={
            productUpdateMutation.isPending || createProductMutation.isPending
          }
          onClick={onSubmit}
        >
          {product?.id ? "Update" : "Next"}
        </Button>
      </DialogCTA>
    </>
  );
}
